import React from 'react';
import { FormattedMessage } from 'react-intl';
import { ofType } from 'redux-observable';
import { EMPTY, of, concat } from 'rxjs';
import { mergeMap } from 'rxjs/operators';

import { noop } from 'ui-components/default-props/noop';
import { GuidService } from 'ui-components/services/guid-service';
import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { DocumentTypeEpicHelpers } from './document-type-epic-helpers';
import { messages } from './messages';
import { showPrinterIsUnavailableNotification } from './ss-connect.helpers';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { LocalStorageService } from '../../services/local-storage-service';
import { PusherService } from '../../services/pusher-service';
import { shipstationConnect } from '../../services/shipstation-connect/';
import Global from '../../store/global-selectors';
import * as Actions from '../../store/shipstation-connect/ss-connect.actions';
import * as PrintSettingsActions from '../../store/print-settings/print-settings.actions';

import { LogService } from '../../services/log-service';
import { DocumentService } from '../../services/document-service';
import { DocumentType } from '../../enums/document-type';
import { PrintingErrorNotification } from '../../pages/settings/connect/notifications/printing-error';
import { compareVersion } from '../../services/compare-version';
import { FeatureFlags } from '../../feature-flags/feature-flags';
import { createPackingSlipsAsync } from '../printing/async-printing/packing-slips';
import { createPickListsAsync } from '../printing/async-printing/pick-lists';

const DEFAULT_SCALE_SETTINGS_KEY = 'v3DefaultScale';

const getMatchingEpicHelper = (docType) => {
  return DocumentTypeEpicHelpers.find((helper) => helper.id === docType);
};

const getPrintProgressNotification = ({ printJob }) => {
  const docType = printJob?.documentType ?? '';
  const jobIds = printJob?.ids || emptyArray;
  const docCount = jobIds.length;
  const selectedPrinterName = printJob?.printer?.name ?? '';
  const baseNotificationProps = {
    notificationType: 'info',
    duration: NOTIFICATION_DURATIONS.SHORT,
    title: <FormattedMessage {...messages.genericPrintingTitle} />,
    content: <FormattedMessage {...messages.sentToPrinter} values={{ selectedPrinterName }} />,
  };

  const actionNotificationProps = getMatchingEpicHelper(docType)?.getDocumentRequestNotificationProps(docCount, selectedPrinterName) ?? {};
  return { ...baseNotificationProps, ...actionNotificationProps };
};

export const printDocumentRequest = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.PRINT_DOCUMENT_REQUEST),
    mergeMap((action) => {
      const { printJob } = action;
      const { shouldSendV3QueueServiceDownloadUrl } = new FeatureFlags({
        state: getState(),
      });

      const shouldUsePackingSlipAsyncWorkflow =
        shouldSendV3QueueServiceDownloadUrl &&
        !printJob?.printUrl &&
        printJob?.documentType === DocumentType.PackingSlip.identifier &&
        printJob?.ids?.length;
      if (shouldUsePackingSlipAsyncWorkflow) {
        return createPackingSlipsAsync({
          fulfillmentPlanIds: printJob.ids,
          getState,
          templateId: printJob.templateId,
          printer: printJob.printer,
        });
      }

      const shouldUsePickListAsyncWorkflow =
        shouldSendV3QueueServiceDownloadUrl &&
        !printJob?.printUrl &&
        printJob?.documentType === DocumentType.PickList.identifier &&
        printJob?.ids?.length;
      if (shouldUsePickListAsyncWorkflow) {
        return createPickListsAsync({
          fulfillmentPlanIds: printJob.ids,
          printer: printJob.printer,
        });
      }

      const shouldRemovePrintUrl =
        !shouldSendV3QueueServiceDownloadUrl &&
        printJob?.printUrl &&
        (printJob?.documentType === DocumentType.PackingSlip.identifier || printJob?.documentType === DocumentType.PickList.identifier);

      if (shouldRemovePrintUrl) {
        // delete any existing printUrl so that we can use the fallback api route in the document enum
        delete printJob.printUrl;
        return of(printJob);
      }

      return of(printJob);
    }),
    mergeMap((printJob) => {
      const state = getState();
      const docType = printJob?.documentType ?? '';
      const printer = printJob?.printer || {};
      const workstations = Global.selectConnectWorkstations(state);
      const connectPrinters = Global.selectConnectFlattenedPrinters(state);

      // check that printer is available
      const isPrinterAvailable = shipstationConnect.isPrinterAvailable(printer, connectPrinters, workstations);
      if (!isPrinterAvailable) {
        LogService.logInfo('print request for offline printer', {
          printJob,
          connectPrinters,
          workstations,
        });
        showPrinterIsUnavailableNotification({
          documentType: docType,
          printer,
        });
        return EMPTY;
      }

      // show progress notification and send print request
      NotificationService.showNotification(getPrintProgressNotification({ printJob }));
      const didRequest = shipstationConnect.printDocument(printJob);

      // show error if request failed to send
      if (!didRequest) {
        LogService.logInfo('print request not sent', {
          printJob,
          workstations,
        });
        showPrinterIsUnavailableNotification({
          documentType: docType,
          printer,
        });
        return EMPTY;
      }

      // optionally update print settings
      const rememberPrintSettings = Global.selectPrintRemeberPrintSettings(state);
      if (rememberPrintSettings) {
        const matchingDocType = DocumentService.getDocumentTypeEnumValueFromIdentifier(docType) || emptyObject;
        const printSettingKey = matchingDocType.printSettingKey || docType;
        return of(PrintSettingsActions.savePrintSettings(printSettingKey, printer));
      }

      return EMPTY;
    }),
  );
};

export const printDocumentFailure = (action$) => {
  return action$.pipe(
    ofType(Actions.PRINT_DOCUMENT_FAILURE),
    mergeMap((action) => {
      const { error, errorMessage, requestId, workstationId } = action;
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        dontHide: true,
        title: <FormattedMessage {...messages.connectPrinterErrorTitle} />,
        content: (
          <PrintingErrorNotification error={error} errorMessage={errorMessage} requestId={requestId} workstationId={workstationId} />
        ),
      });
      return EMPTY;
    }),
  );
};

export const saveDefaultScaleToLocalStorage = (action$) => {
  return action$.pipe(
    ofType(Actions.SET_DEFAULT_SCALE),
    mergeMap(({ defaultScale }) => {
      LocalStorageService.setItem(DEFAULT_SCALE_SETTINGS_KEY, defaultScale);
      return EMPTY;
    }),
  );
};

export const restoreDefaultScale = (action$) => {
  return action$.pipe(
    ofType(Actions.INITIATE_RESTORE_DEFAULT_SCALE),
    mergeMap(() => {
      const defaultScale = LocalStorageService.getItem(DEFAULT_SCALE_SETTINGS_KEY);
      if (defaultScale) {
        return of(Actions.restoreDefaultScale(defaultScale));
      }
      return EMPTY;
    }),
  );
};

export const handleWorkstationRemovedMessage = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.WORKSTATION_REMOVED),
    mergeMap(({ workstationId }) => {
      const actions = [of(Actions.getWorkstationsRequest()), of(Actions.clearDevicesForWorkstation(workstationId))];

      // if workstation is using Connect v4.x we don't want to clear the print settings
      // because the workstation removed message might have been triggered by a
      // 'Refresh Connection' click in the Connect UI. If that's the case, the workstation
      // is likely to be immediately re-added by the 'client-connect:broadcast:printers' message
      const workstation = Global.selectConnectWorkstationById(getState(), {
        workstationId,
      });
      const isUsingConnectVersion4orGreater = workstation.version?.split('.')?.[0] >= 4;

      if (!isUsingConnectVersion4orGreater) {
        actions.push(of(PrintSettingsActions.clearPrintSettingsWithWorkstation(workstationId)));
      }

      return concat(...actions);
    }),
  );
};

export const checkPusherChannels = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_WORKSTATIONS_SUCCESS),
    mergeMap(({ workstations, isInitialLoad }) => {
      // handle workstation setup in the appInitialization epic at startup
      if (isInitialLoad) {
        return EMPTY;
      }

      const needsReplyToChannel = workstations.some((w) => compareVersion(w.version, '4.10.0') > -1);

      if (PusherService.isInitialized() && !PusherService.replyToChannelName && needsReplyToChannel) {
        PusherService.subscribeToReplyTo(GuidService.newGuid(), noop);
      }

      return EMPTY;
    }),
  );
};
